import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Badge } from '../ui/badge'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import Typing from './Typing'
import { SenderType } from '@/constants/chat'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import { formatDateRelative, formatTime } from '@/utils/dateTimeUtils'
import AdvisorMessagesSkeleton from './AdvisorMessagesSkeleton'
import { UserRole } from '@/constants/room'
import { useAppDispatch } from '@/redux/hooks'
import { loadMoreAdvisorMessages } from '@/redux/asyncThunks/websocketThunks'
import React from 'react'
import { cn } from '@/lib/utils'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import LlmModelBadge from '@/components/OGE/LlmModelBadge'

const AdvisorMessages: React.FC = React.memo(() => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true)
  const [isUserNearBottom, setIsUserNearBottom] = useState(true)
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false)
  const wasNearBottomWhenGeneratingStarted = useRef<boolean>(true)
  const { teams } = useSelector((state: RootState) => state.team)

  const advisorMessages = useSelector(
    (state: RootState) => state.chat.advisorChatMessages
  )
  const { roomMembers } = useSelector((state: RootState) => state.room)
  const { user } = useEffectiveUser()
  const roomPlayers = useSelector(selectRoomPlayers)
  const isGenerating = useSelector(
    (state: RootState) => state.chat.isGenerating
  )
  const isAdvisorMessagesLoading = useSelector(
    (state: RootState) => state.chat.loadingAdvisorMsgs
  )

  const advisorChatHasMore = useSelector(
    (state: RootState) => state.chat.advisorChatHasMore
  )
  const advisorChatCurrentPage = useSelector(
    (state: RootState) => state.chat.advisorChatCurrentPage
  )
  const advisorChatLoadingMore = useSelector(
    (state: RootState) => state.chat.advisorChatLoadingMore
  )

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight
    }
  }
  const checkIfNearBottom = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        messagesContainerRef.current
      const threshold = 100
      return scrollHeight - scrollTop - clientHeight < threshold
    }
    return true
  }

  useEffect(() => {
    if (shouldScrollToBottom && isUserNearBottom && !userHasScrolledUp) {
      scrollToBottom()
    }
  }, [
    advisorMessages,
    shouldScrollToBottom,
    isUserNearBottom,
    userHasScrolledUp,
  ])

  useEffect(() => {
    if (isGenerating) {
      wasNearBottomWhenGeneratingStarted.current = checkIfNearBottom()

      if (wasNearBottomWhenGeneratingStarted.current) {
        scrollToBottom()
        setIsUserNearBottom(true)
        setUserHasScrolledUp(false)
      }
    }
  }, [isGenerating])

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop } = messagesContainerRef.current
      const wasNearBottom = checkIfNearBottom()

      setIsUserNearBottom(wasNearBottom)

      if (!wasNearBottom) {
        setUserHasScrolledUp(true)
      } else if (wasNearBottom && userHasScrolledUp) {
        setUserHasScrolledUp(false)
        setShouldScrollToBottom(true)
      }

      if (scrollTop === 0 && advisorChatHasMore && !advisorChatLoadingMore) {
        setShouldScrollToBottom(false)
        dispatch(
          loadMoreAdvisorMessages({
            page: (advisorChatCurrentPage || 1) + 1,
          })
        )
      }
    }
  }

  useEffect(() => {
    if (!advisorChatLoadingMore) {
      if (checkIfNearBottom()) {
        setShouldScrollToBottom(true)
      }
    }
  }, [advisorChatLoadingMore])

  useEffect(() => {
    if (!isAdvisorMessagesLoading && advisorMessages.length > 0) {
      scrollToBottom()
    }
  }, [isAdvisorMessagesLoading])

  const getPlayerTeam = useCallback(
    (email: string) => {
      const player = roomPlayers.find((player) => player.member_email === email)
      const team = teams.find((t) => t.id === player?.teamId)
      return team?.name || `Team ${player?.teamId}`
    },
    [roomPlayers, teams]
  )
  const getMemberName = useCallback(
    (email: string) => {
      const member = roomMembers.find((member) => member.member_email === email)
      return member?.member_name || email
    },
    [roomMembers]
  )

  return (
    <div
      ref={messagesContainerRef}
      className='overflow-auto flex-1'
      onScroll={handleScroll}
    >
      {advisorChatLoadingMore && (
        <div className='flex flex-col justify-center items-center py-8 mx-4 rounded-lg border-2 border-gray-300 border-dashed bg-gray-50/50'>
          <div className='mb-3 w-12 h-12 rounded-full border-4 border-blue-500 animate-spin border-t-transparent'></div>
          <p className='font-medium text-gray-600'>Loading more messages...</p>
        </div>
      )}

      {isAdvisorMessagesLoading
        ? user?.role && (
            <AdvisorMessagesSkeleton
              isProfessor={user.role === UserRole.Professor}
            />
          )
        : advisorMessages?.map((message, index) => {
            const isProfessorMessage =
              message.sender_type === SenderType.Professor
            const isPlayerMessage = message.sender_type === SenderType.Player

            const showDate =
              index === 0 ||
              (message.created_at &&
                advisorMessages[index - 1]?.created_at &&
                formatDateRelative(message.created_at) !==
                  formatDateRelative(
                    advisorMessages[index - 1].created_at as string
                  ))
            return (
              <div key={`${message.id}-${index}`}>
                {showDate && message.created_at && (
                  <div className='flex justify-center items-center my-4'>
                    <div className='px-3 py-1 text-xs font-medium text-gray-600 bg-gray-200 rounded-full'>
                      {formatDateRelative(message.created_at)}
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    'flex mb-4',
                    message.user === user?.email
                      ? 'justify-end'
                      : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[80%] p-4 rounded-[20px]',
                      isProfessorMessage && 'bg-[#e2ece4]',
                      isPlayerMessage && 'bg-[#f7d4e2]',
                      !isProfessorMessage && !isPlayerMessage && 'bg-[#E6ECF1]'
                    )}
                  >
                    <div className='flex gap-3 items-center mb-2'>
                      <Avatar className='w-8 h-8'>
                        <AvatarImage
                          src='/images/avatar-image.svg'
                          alt='Avatar'
                        />
                        <AvatarFallback>CN</AvatarFallback>
                      </Avatar>
                      <div className='flex items-center'>
                        <span className='flex items-center text-base font-medium text-dark-blue whitespace-break-spaces'>
                          <div className='flex flex-col mr-2'>
                            {message.user && getMemberName(message.user)}
                            {message.user &&
                              isPlayerMessage &&
                              getPlayerTeam(message.user) && (
                                <span className='text-xs text-gray-500'>
                                  {getPlayerTeam(message.user)}
                                </span>
                              )}
                          </div>

                          {isProfessorMessage && (
                            <Badge className='ml-2 pointer-events-none hover:bg-none'>
                              white-cell
                            </Badge>
                          )}
                          {isPlayerMessage && (
                            <Badge className='ml-2 pointer-events-none hover:bg-none'>
                              Player
                            </Badge>
                          )}
                        </span>
                      </div>
                    </div>
                    <div
                      className={cn(
                        'text-sm font-medium text-black',
                        message.streaming &&
                          message.sender_type === SenderType.Chatbot &&
                          'animate-pulse'
                      )}
                    >
                      <div className='chat-markdown prose prose-sm max-w-none [&_a]:break-all [&_*]:break-words overflow-hidden'>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                          components={{
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {message.message}
                        </ReactMarkdown>
                      </div>
                      {message.streaming &&
                        message.sender_type === SenderType.Chatbot &&
                        '▋'}
                    </div>
                    {(() => {
                      const isBot =
                        message.sender_type === SenderType.Chatbot
                      const hasModelInfo =
                        !!message.llm_model_name || !!message.llm_provider
                      const showFooter =
                        !!message.created_at || (isBot && hasModelInfo)
                      if (!showFooter) return null
                      return (
                        <div className='flex justify-end items-center gap-2 mt-2'>
                          {isBot &&
                            hasModelInfo &&
                            user?.role === UserRole.Professor && (
                              <LlmModelBadge
                                llmModelName={message.llm_model_name}
                                llmProvider={message.llm_provider}
                              />
                            )}
                          {message.created_at && (
                            <span className='text-xs text-gray-500 opacity-75'>
                              {formatTime(message.created_at)}
                            </span>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>
            )
          })}

      <div ref={messagesEndRef} />

      {(() => {
        const lastMsg = advisorMessages[advisorMessages.length - 1]

        const isUserLast =
          lastMsg &&
          lastMsg.sender_type === SenderType.Player &&
          lastMsg.sendToProfessor === false &&
          isGenerating

        const advisorStreaming = advisorMessages.some(
          (msg) => msg.sender_type === SenderType.Chatbot && msg.streaming
        )

        if (isUserLast && !advisorStreaming) {
          return (
            <div className='flex justify-start px-4 mb-4'>
              <Typing />
            </div>
          )
        }

        return null
      })()}
    </div>
  )
})

export default AdvisorMessages
